import React, { useEffect, useState, useRef } from 'react';
import Footer from './Footer';
import InfoButton from './InfoButton.js';
import { getMarkDown, getInlineMarkDown, getMarkDownWithInfoButton, useDetectMobile, formatCardLoadTiming, useDetectIOS,useDetectAndroid, getButtonWithArrow } from '../utils';
import '../../../stylesheets/triagebot/SingleSelect.scss';
import ChangeOverDialog from '../dialogs/ChangeOverDialog';
import { useTiming } from '../TimingContext';
import { useCardLoadTiming } from '../hooks/useCardLoadTiming';
import Checkmark from '../../../assets/checkmark';


const PromptMessage = ({ message, sendMessage, sentMessage, setToastMessage, selection, disabled, allowEdits, keepAlive }) => {
    const [selected, setSelected] = useState(selection);
    const [submitted, setSubmitted] = useState(sentMessage);
    const [editing, setEditing] = useState(false);
    const [showInPersonChangeOverPromptDialog, setShowInPersonChangeOverPromptDialog] = useState(false);
    const [IsLoading,setIsLoading] = useState(false);

    const {isIOS} = useDetectIOS();
    const {isAndroid} = useDetectAndroid();
    

    const isCarePlanCard = (message?.question_id === "TreatmentPlan");
    const explication = (message?.data?.[0]?.explication)
        ? message.data[0].explication
        : '';

    const header = message?.data?.[0]?.headerText 
            ?   message?.data?.[0]?.headerText 
            : '';
    const cdoName = message?.data?.[0]?.cdoName 
    ?   message?.data?.[0]?.cdoName 
    : '';
    const subHeaderText = (message?.data?.[0]?.subHeaderText)
        ? message.data[0].subHeaderText
        : '';
    
    const questionId = (message?.data?.[0]?.question_id)
        ? message.data[0].question_id
        : '';

    const hasSubHeader = Boolean(subHeaderText);
    const subHeaderId = hasSubHeader ? `${questionId || 'single-select'}-subheader` : undefined;

    const currentStep = message?.data?.[0]?.current_step
        ? message.data[0].current_step
        : '';

    const infoMean = message?.data?.[0]?.infoHeadText || message?.infoHeadText || 'What does this mean?';


    const hasInfoLink = message?.data?.[0]?.info?.linkText;

    const changeOverPromptDialogInPerson = message?.data?.[1]?.changeoverPrompt;

    const infoType = hasInfoLink
        ? message.data[0].info.type
        : ''

    const infoLinkText = hasInfoLink
        ? message.data[0].info.linkText
        : ''

    const infoHeaderText = hasInfoLink
        ? message.data[0].info.headerText
        : ''

    const infoBody = hasInfoLink
        ? message.data[0].info.text
        : ''

    const infoLinkMessage = {
        type: infoType || '',
        header: infoHeaderText || '',
        body: infoBody || ''
    }

    const HaveCancelButton = message?.data?.[0]?.cancelButton?.displayText
            ? message?.data?.[0]?.cancelButton.displayText
            : '';

    const HaveNextButton = message?.data?.[0]?.nextButton?.displayText
            ? message?.data?.[0]?.nextButton.displayText
            : '';

    const isDisabled = submitted || disabled;

    const isYesButton = (button) => {
        return button && button.value && ['present', 'yes'].includes(button.value.toLowerCase());
    }

    const isNoButton = (button) => {
        return button && button.value && ['absent', 'no'].includes(button.value.toLowerCase());
    }

    const isUnknownButton = (button) => {
        return button && button.value && ['unknown', "i don't know"].includes(button.value.toLowerCase());
    }

    const isYesNo = () => {
        return message.buttons.reduce((result, button) => {
            return result && (
                isYesButton(button)
                || isNoButton(button)
                || isUnknownButton(button)
            )
        }, true)
    }

    const prompt = useRef();
    const headerRef = useRef(null);
    const [isFirstLoadedCard, setIsFirstLoadedCard] = useState(false);

    const buttonPress = (event, button) => {
        if (submitted) return;

        if(HaveCancelButton || HaveNextButton){
            setSelected(button.value);
            return;
        }

        const selectedButton = event.currentTarget;
        selectedButton?.parentElement?.classList.add('hide-unselected');
        selectedButton?.classList.add('selected');

        setSelected(button.value);
        setSubmitted(!keepAlive);
        setEditing(false);

        if (button.type === "openUrl") {
            window.open(button.hyperlink, '_self');
            return;
        }

        // if (button.value.toLowerCase() === 'urgentcare' && (button.redirectUrl || button.iOSRedirectUrl)) {
        //     let redirectUrl;

        //     if (isIOS && button.iOSUrgentCareURL) {
        //         // For iOS iPhone/iPad
        //         redirectUrl = button.iOSUrgentCareURL;
        //     } 
        //     else if (isAndroid && button.androidUrgentCareURL) {
        //         // For Android Phone/Tab
        //         redirectUrl = button.androidUrgentCareURL;
        //     } else {
        //         // Fallback to button.redirectUrl if no platform-specific URL is available
        //         redirectUrl = button.redirectUrl;
        //     }

        //     console.log('Redirecting to MyChart On My Way:', redirectUrl);
            
        //     // Use window.location.href for deep links (iOS) or window.open for web URLs
        //     if (isIOS || isAndroid) {
        //         window.location.href = redirectUrl;
        //     } else {
        //         window.open(redirectUrl, '_blank');
        //     }
        // }

        const redirectValues = ['urgentcare', 'virtualcare', 'inperson'];
        
        if (button.redirectUrl && redirectValues.includes(button.value.toLowerCase())) {
            try {
                const url = new URL(button.redirectUrl);
                console.log('Redirecting to deeplink:', url.toString());
                window.open(url.toString(), '_blank');
            } catch (e) {
                console.error('Invalid redirect URL:', button.redirectUrl, e);
            }
        }

        editing
            ? sendMessage('message', `##edit@${message?.data?.[0]?.question_id}@${button.value || 'none'}`, `##edit@${message?.data?.[0]?.question_id}@${button.value || 'none'}`, cardName)
            : sendMessage('message', button.value || 'none', button.value || 'none', cardName);
    }

    const changeAccepted = () => {
        if ((submitted || questionId >= 0)) {
            sendMessage('rewind', questionId, questionId);
            setSubmitted(false);
            setEditing(true);
            setSelected('');
            return;
        }
    };

    useEffect(() => {
        setSubmitted(sentMessage);
        setSelected(selection);
    }, [sentMessage, selection])

    useEffect(() => {
        if (!selection && document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
    }, [selection]);

    const { logTiming } = useTiming();
    const cardName = (message.data && message.data[0] && message.data[0].question_id) ? message.data[0].question_id : "Prompt";
     useCardLoadTiming({
                  sentAt: message._sentAt,
                  cardName,
                  logTiming,
                  formatCardLoadTiming
              });

    const {isMobile} = useDetectMobile();

    const CancelButton = message?.data?.[0]?.cancelButton;

    const cancelPress = () => {
        sendMessage('rewind', ['summaryPlaceHolder', 'emergency'], ['summaryPlaceHolder', 'emergency'], cardName);
        sendMessage('message', CancelButton.value, CancelButton.value, cardName);
    }

    const submitPress = (selected) => {
        if (submitted) return;

        if(selected==="InPerson" || selected==="Continue"){
            setIsLoading(true);
        }

        if(changeOverPromptDialogInPerson!==undefined && (selected === "InPerson")){
            setShowInPersonChangeOverPromptDialog(true);
            return;
        }
        if((selected.toLowerCase() === "other") && (cdoName.toLowerCase()==="nwest" || cdoName.toLowerCase()==="optumwashington")){
            if(changeOverPromptDialogInPerson!==undefined) {
                setShowInPersonChangeOverPromptDialog(true);
                return;
            }
            setIsLoading(true);
        }

        setSubmitted(true);

        sendMessage('message', selected, selected, cardName);
    }

    const dialogResponse = (response) => {
        if (response) {
            setSubmitted(true);

            return sendMessage('message', selected, selected, cardName);
        }
    }

    useEffect(() => {
        setIsFirstLoadedCard(document.querySelectorAll('.card').length === 1);
    }, []);

    useEffect(() => {
        if (isDisabled) return;
        if (isFirstLoadedCard) return;

        const focusTimer = setTimeout(() => {
            headerRef.current?.focus({ preventScroll: true });
        }, 500);

        return () => clearTimeout(focusTimer);
    }, [cardName, isDisabled, isFirstLoadedCard]);

    useEffect(() => {
        if (!IsLoading) return;
        if (message == null) return; // transient state: don't decide yet
        if (message.isLastMessage === false) {
            setIsLoading(false);
        }
    }, [message, IsLoading]);

    const getButtonExplication = (button) => {
    if (!button?.explication) return null;
    const explication = (button.explication?.text) ? button.explication.text : button.explication;
    if (typeof explication === 'string' && explication && explication !== "none") {
        return explication;
    }
    return null;
    };

    return (
        <div ref={prompt} className={(submitted ? 'disabled ' : '') + 'single-select card'}>
            <div className='content' >
                <div className='header' id={ questionId }>
                    <h2 ref={headerRef} className='headerText headerTextColor' tabIndex={isDisabled ? -1 : (isFirstLoadedCard ? 0 : -1)}>
                        {
                             (explication && explication!=="none") 
                                ? getMarkDownWithInfoButton(message.text, explication, setToastMessage, isDisabled, infoMean)
                                : getInlineMarkDown(message.text || header)
                        }
                    </h2>
                    {
                        hasSubHeader &&
                        <div id={subHeaderId} className='subheader'>
                            {getMarkDown(subHeaderText|| message.subHeaderText)}
                        </div>
                    }
                </div>
                <div className='body' role='group' aria-label='Answer options' aria-describedby={subHeaderId}>
                    {
                        message.buttons.map((button, key) => {
                            const isSelected = (selected === button.value) && 'selected';
                            const icon = (isYesButton(button) && 'yes') ||
                                (isNoButton(button) && 'no') ||
                                (isUnknownButton(button) && 'idk')
                            const explicationText = getButtonExplication(button);
                            return isYesNo()
                                ?
                                <div key={button.value} className='button-row' >
                                    <button
                                        key={button.value}
                                        className={isSelected + ' single-select ' + icon}
                                        onClick={(event) => buttonPress(event, button)}
                                        onMouseDown={(e) => e.preventDefault()}
                                        disabled={isDisabled || button.disabled}
                                        aria-label={button.displayText}
                                        aria-pressed={isSelected ? 'true' : 'false'}
                                    >
                                        <span>{button.displayText}</span>
                                    </button>
                                </div>
                                :
                                <div key={button.value} className='button-row' >
                                    <button
                                        className={isSelected + ' single-select '+(button.disabled? 'virtualdisabled' : '')}
                                        onClick={(event) => buttonPress(event, button)}
                                        disabled={isDisabled || button.disabled}
                                        aria-label={button.displayText}
                                        aria-pressed={isSelected ? 'true' : 'false'}
                                    >
                                        <span className='single-select-input' aria-hidden="true" />
                                        {
                                            isSelected  &&
                                            <Checkmark
                                                className='checkmark'
                                                role="img"
                                                ariaLabelledby={button.displayText + (isSelected ? "-selected" : "")}
                                                id={button.displayText}
                                            />
                                        }
                                        <span>{button.displayText}</span>
                                    </button>
                                    {
                                     explicationText && 
                                            <InfoButton
                                                disabled={isDisabled}
                                                header={infoMean}
                                                ariaText={button.displayText}
                                                explication={explicationText}
                                                onShowInfo={setToastMessage}
                                            />
                                    }
                                </div>
                        })
                    }
                    {
                        hasInfoLink &&
                        (isDisabled
                            ?
                            <a className={'disabled infoLink'}>
                                {infoLinkText}
                            </a>
                            :
                            <a
                                href={'#'}
                                className={'infoLink'}
                                onClick={() => { setToastMessage(infoLinkMessage) }}
                            >
                                {infoLinkText}
                            </a>
                        )
                    }
                </div>
            </div>
            {
                (submitted && questionId) && 
                <div style={{ marginTop: 24 }}>
                    <Footer
                        showChangeButton={allowEdits && !message.isLastMessage}
                        onAccepted={changeAccepted}
                        onDenied={() => sendMessage('fastforward')}
                        disabled={disabled}
                        divID={questionId}
                        message={message}
                    >
                    </Footer>
                </div>
            }

            { (HaveCancelButton || HaveNextButton) &&
            <div style={submitted ? { marginTop : '0px' } : { marginTop: '24px' }} className={(isMobile ? 'mobile ' : '') + 'footer-next-cancel-button'}>
                <Footer>
                    <button
                        onClick={cancelPress}
                        className={isMobile ? 'mobile cancel-button' : 'cancel-button'}
                        disabled={isDisabled}
                    >
                        {HaveCancelButton}
                        </button>
                    <button
                        onClick={()=>submitPress(selected)}
                            className='continue-button button-footer-width-next'
                        disabled={isDisabled || !selected}
                    >
                        {getButtonWithArrow(HaveNextButton)}
                        </button>
                </Footer>
            </div>
            }

            {
                showInPersonChangeOverPromptDialog
                ?
                    <ChangeOverDialog
                        message={changeOverPromptDialogInPerson}
                        setShowChangeOverPromptDialog={setShowInPersonChangeOverPromptDialog}
                        isAccepted={dialogResponse}
                    />
                :
                    <></>
            }
            {
                IsLoading && <div className="loader">
                <svg className="spinner" width="65px" height="65px" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
                    <circle className="path" fill="none" strokeWidth="6" strokeLinecap="round" cx="33" cy="33" r="30"></circle>
                </svg>
                </div>
            }
        </div >
    );
}

export default PromptMessage;